<template>
  <div :id="rootElementId" :style="`height:${SVG_RENDER_HEIGHT}px`"></div>
</template>

<script>
import ResizeObserver from 'resize-observer-polyfill';
import * as d3 from 'd3v4';
import { getDataUrl, getPositionCode, getChipColorByPosition } from '../../utils/functions';
import { loadFonts, copyAndAppendElement } from '../../utils/exportToPNG';

export default {
  name: 'PassMapChart',
  props: {
    id: {
      type: [Number, String],
      required: true
    },
    size: {
      type: Number,
      required: false,
      default: 380
    },
    margin: {
      type: Object,
      required: false,
      default: () => ({ top: 0, right: 0, bottom: 0, left: 0 })
    },
    passes: {
      type: Array,
      required: true
    },
    fontFamily: {
      type: String,
      default: 'NeueMontreal'
    },
    mediaMode: {
      type: Boolean,
      default: false
    },
    colorsLegend: {
      type: Object,
      required: false,
      default: null
    },
    defaultColor: {
      type: String,
      default: '#3366FF'
    },
    legendTitle: {
      type: String,
      default: ""
    },
    legendSubtitle: {
      type: String,
      default: ""
    },
    legendTeamCrest: {
      type: String,
      default: 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png'
    },
    legendTeamName: {
      type: String,
      default: ''
    },
    legendMetrics: {
      type: Object,
      default: null
    },
    showCircles: {
      type: Boolean,
      default: true
    },
    showLines: {
      type: Boolean,
      default: true
    },
    orientation: {
      type: String,
      default: 'horizontal' // 'vertical'
    },
  },
  data() {
    return {
      height: 0,
      width: 0,
      images: {},
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    SVG_WIDTH() {
      return this.size;
    },
    PITCH_WIDTH() {
      return this.SVG_WIDTH - this.margin.left - this.margin.right;
    },
    PITCH_HEIGHT() {
      return this.PITCH_WIDTH * 0.67125;
    },
    isVertical () {
      return this.orientation === 'vertical';
    },
    SVG_RENDER_WIDTH () {
      return this.isVertical ? this.PITCH_HEIGHT : this.SVG_WIDTH;
    },
    SVG_RENDER_HEIGHT () {
      return this.isVertical ? this.PITCH_WIDTH : this.PITCH_HEIGHT;
    },
    rootElementId() {
      return `pitchPassMap-${this.id}`;
    },
    currentUserFieldColor() {
      return this.$store.getters['auth/fieldColor'];
    },
    currentUserLanguage() {
      return this.$store.getters['auth/language'];
    },
    dinamicOpacity() {
      return this.showLines ? 1 : 0;
    }
  },
  async mounted() {
    this.observer = new ResizeObserver(this.onResize).observe(
      document.getElementById(this.rootElementId)
    );
    await this.render();
  },
  methods: {
    t(key, fallback) {
      if (this.$i18n && this.$i18n.t) {
        try {
          return this.$i18n.t(key);
        } catch (e) {
          return fallback || key;
        }
      }
      return fallback || key;
    },
    getPositionCode(position) {
      return getPositionCode((this.currentUser && this.currentUser.fieldPositionCodeId != null) ? this.currentUser.fieldPositionCodeId : 1, position);
    },
    getPositionChip(position) {
      return getChipColorByPosition(position);
    },
    async render() {
      const SVG_WIDTH = this.SVG_WIDTH;
      const margin = this.margin;
      const PITCH_WIDTH = this.PITCH_WIDTH;
      const PITCH_HEIGHT = this.PITCH_HEIGHT;

      d3.selectAll('.lbp-tooltip').remove();
      d3.select('body')
        .append('div')
        .attr('class', 'lbp-tooltip')
        .style('position', 'fixed')
        .style('display', 'none')
        .style('pointer-events', 'none')
        .style('background', 'rgba(255,255,255,0.96)')
        .style('color', '#111111')
        .style('border', '1px solid rgba(0,0,0,0.15)')
        .style('box-shadow', '0 6px 18px rgba(0,0,0,0.18)')
        .style('border-radius', '6px')
        .style('padding', '12px 16px')
        .style('text-align', 'center')
        .style('z-index', 9999);

      // remove prev svg
      d3.select(`#${this.rootElementId} > svg`).remove();

      const currentWidth = this.isVertical ? (PITCH_HEIGHT + margin.left + margin.right) : SVG_WIDTH;
      const currentHeight = this.isVertical ? (PITCH_WIDTH + margin.top + margin.bottom) : (PITCH_HEIGHT + margin.top + margin.bottom);

      const svg = d3
        .select(`#${this.rootElementId}`)
        .append('svg')
        .attr('width', currentWidth)
        .attr('height', currentHeight)
        .style('background-color', '#ffffff')
        .attr('class', 'pitch')
        .attr('id', `${this.rootElementId}SVG`);

      svg.selectAll('g.pitch-layer').remove();
      const pitchLayer = svg.append('g').attr('class', 'pitch-layer');
      
      if (this.isVertical) {
        pitchLayer.attr('transform', `translate(${margin.left}, ${margin.top + PITCH_WIDTH}) rotate(-90)`);
      } 
      else {
        pitchLayer.attr('transform', `translate(${margin.left}, ${margin.top})`);
      };

      await this.drawPitch(pitchLayer, 0, 0, PITCH_WIDTH, PITCH_HEIGHT);

      this.renderLegend(svg, false, true, false, false);

      await this.drawLogo(svg, PITCH_WIDTH, PITCH_HEIGHT, margin);

      // Render Passes
      if (this.passes && this.passes.length && (this.showCircles || this.showLines)) {
        this.renderPasses(pitchLayer, PITCH_WIDTH, PITCH_HEIGHT, this.passes);
      };

      await this.drawAttackArrow(svg, this.SVG_RENDER_WIDTH, this.SVG_RENDER_HEIGHT, margin);

      const font = await loadFonts(this.fontFamily);
      copyAndAppendElement(svg.node(), font.node(), 0, 0);
    },
    async drawPitch(svg, x, y, width, height) {
      const imgPath = require(`@/assets/img/pitch_${this.currentUserFieldColor}.png`);
      await this.drawImageBase64(svg, x, y, width, height, imgPath, 'pitch');
    },
    renderPasses(svg, chartWidth, chartHeight, passes) {
      const self = this;

      svg.selectAll('g.lbp-layer').remove();
      const layer = svg.append('g').attr('class', 'lbp-layer');

      const x = d3.scaleLinear()
        .domain([0 - (100 * 0.093), 100 + (100 * 0.10)])
        .range([0, chartWidth]);

      const y = d3.scaleLinear()
        .domain([0 - (100 * 0.105), 100 + (100 * 0.105)])
        .range([chartHeight, 0]);

      const circleRadius = chartWidth * 0.006;
      const hoverOnlyLines = false;

      let tooltip = d3.select('body').select('div.lbp-tooltip');
      if (tooltip.empty && tooltip.empty()) {
        tooltip = d3.select('body')
          .append('div')
          .attr('class', 'lbp-tooltip')
          .style('position', 'fixed')
          .style('display', 'none')
          .style('pointer-events', 'none')
          .style('background', 'rgba(255,255,255,0.96)')
          .style('color', '#111')
          .style('border', '1px solid rgba(0,0,0,0.15)')
          .style('box-shadow', '0 6px 18px rgba(0,0,0,0.20)')
          .style('border-radius', '6px')
          .style('padding', '10px 12px')
          .style('z-index', 9999)
          .style('text-align', 'center');
      }

      let passSel = layer.selectAll('g.lbp-pass').data(passes || [], function (d) { return d.passId; });
      passSel.exit().remove();

      const passEnter = passSel.enter().append('g').attr('class', 'lbp-pass');
      passSel = passEnter.merge(passSel);

      passSel.each(function (p) {
        const passG = d3.select(this);

        const sx = x(p.x);
        const sy = y(p.y);
        const ex = x(p.x_end);
        const ey = y(p.y_end);

        const isFailed = p.o === false;
        const strokeColor = isFailed ? '#FF0000' : '#28e628';

        if (self.showLines || hoverOnlyLines) {
          self.ensurePassLine(passG, p, sx, sy, ex, ey, chartWidth);
          passG.selectAll('.lbp-line').style('opacity', self.dinamicOpacity);
        }
        else {
          passG.selectAll('.lbp-arrow-line').remove();
          passG.selectAll('path.lbp-line-tip').remove();
        }

        const showFixedCircle = self.showCircles && !isFailed;
        const circleData = showFixedCircle ? [p] : [];

        let circleSel = passG.selectAll('circle.lbp-circle').data(circleData, function (d) { return d.passId; });
        circleSel.exit().remove();

        const circleEnter = circleSel.enter()
          .append('circle')
          .attr('class', 'lbp-circle')
          .style('stroke', '#000')
          .style('fill-opacity', 0.8);

        circleSel = circleEnter.merge(circleSel);
        circleSel
          .attr('cx', sx)
          .attr('cy', sy)
          .attr('r', circleRadius)
          .style('fill', strokeColor)
          .style('stroke-width', circleRadius / 4);

        const showCross = self.showCircles && isFailed;
        const crossData = showCross ? [p] : [];

        let crossSel = passG.selectAll('path.lbp-start-cross').data(crossData, function (d) { return d.passId; });
        crossSel.exit().remove();

        if (showCross) {
          const crossEnter = crossSel.enter()
            .append('path')
            .attr('class', 'lbp-start-cross')
            .style('pointer-events', 'all')
            .style('cursor', 'pointer');

          crossSel = crossEnter.merge(crossSel);
          crossSel.each(function () {
            const sel = d3.select(this);
            const crossSize = chartWidth * 0.15;
            const cross = d3.symbol().type(d3.symbolCross).size(crossSize);

            sel
              .attr('d', cross)
              .attr('transform', `translate(${sx},${sy}) rotate(-45)`)
              .attr('fill', strokeColor)
              .attr('stroke', strokeColor)
              .attr('stroke-width', 0.1);
          });
        }

        const handleMouseEnter = function (event) {
          const target = d3.select(this);
          if (target.node().tagName === 'circle') {
            target.transition().duration(200).attr('r', circleRadius * 1.4);
          } else {
            target.transition().duration(200).style('opacity', 1);
          }
          target.style('cursor', 'pointer');

          if (hoverOnlyLines) {
            if (passG.raise) passG.raise();
            passG.selectAll('.lbp-arrow-line, path.lbp-line-tip').transition().duration(120).style('opacity', self.dinamicOpacity || 1);
          }

          const textLine1 = p.tName;
          const textLine2 = p.pN;
          const textLine3 = p.gN;
          const textLine4 = `${self.$t("eventChart.pass")} @ ${p.min}:${p.sec}`;
          const colorText5 = p.o === true ? '#148545' : '#e53e3e';
          const textLine5 = `<span style="color: ${colorText5}; font-weight: bold;">${
            p.o === true ? self.$t("eventChart.successfulEvent") : self.$t("eventChart.unsuccessfulEvent")
          }</span>`;

          const tooltipLines = [
            `<strong>${textLine1}</strong>`,
            textLine2,
            textLine3,
            textLine4,
            textLine5
          ].filter(Boolean);

          const widthTooltip = 250;
          const rect = this.getBoundingClientRect();
          const r = target.node().tagName === 'circle' ? (parseFloat(target.attr('r')) || circleRadius) : 6;

          let left = rect.left + r - (widthTooltip / 2);
          left = Math.max(8, Math.min(window.innerWidth - widthTooltip - 8, left));

          let estimatedHeight = 80;
          let top = rect.top - estimatedHeight - 10;
          if (top < 8) {
            top = rect.top + r + 10;
          }
          const isMobile = self.$vuetify && self.$vuetify.breakpoint && self.$vuetify.breakpoint.mobile;
          if (isMobile) {
            left = window.innerWidth / 2 - widthTooltip / 2;
            top = Math.max(8, rect.top - estimatedHeight - 10);
          }

          tooltip
            .style('width', widthTooltip + 'px')
            .style('height', 'auto')
            .style('min-height', 'auto')
            .style('left', left + 'px')
            .style('top', top + 'px')
            .style('display', 'inline-block')
            .style('font-size', '14px')
            .style('padding-bottom', '20px')
            .style('font-family', self.fontFamily)
            .style('font-weight', 500)
            .html(tooltipLines.join('<br/>'));
        };

        const handleMouseOut = function () {
          const target = d3.select(this);
          if (target.node().tagName === 'circle') {
            target.transition().duration(200).attr('r', circleRadius);
          }

          if (hoverOnlyLines) {
            passG.selectAll('.lbp-arrow-line, path.lbp-line-tip')
              .transition()
              .duration(120)
              .style('opacity', 0);
          }

          tooltip.style('display', 'none');
        };

        circleSel.on('mouseenter', handleMouseEnter).on('mouseout', handleMouseOut);
        crossSel.on('mouseenter', handleMouseEnter).on('mouseout', handleMouseOut);
      });
    },

    ensurePassLine(passG, p, sx, sy, ex, ey, chartWidth) {
      const isFailed = p.o === false;
      const strokeColor = isFailed ? '#FF0000' : '#28e628';
      const lineWidth = 0.003 * chartWidth;

      let lineSel = passG.selectAll('line.lbp-arrow-line')
        .data([p], function (d) { return d.passId || d.gId; });

      lineSel.exit().remove();

      const lineEnter = lineSel.enter()
        .append('line')
        .attr('class', 'lbp-line lbp-arrow-line')
        .style('pointer-events', 'none');

      lineSel = lineEnter.merge(lineSel);

      lineSel
        .attr('x1', sx)
        .attr('y1', sy)
        .attr('x2', ex)
        .attr('y2', ey)
        .attr('stroke-width', lineWidth)
        .attr('stroke', strokeColor);

      // Arrow Head
      const angleRadians = Math.atan2(ey - sy, ex - sx);
      const verticalTransform = (angleRadians + (Math.PI / 2)) * (180 / Math.PI);

      let tipSel = passG.selectAll('path.lbp-line-tip')
        .data([p], function (d) { return d.passId || d.gId; });

      tipSel.exit().remove();

      const tipEnter = tipSel.enter()
        .append('path')
        .attr('class', 'lbp-line lbp-line-tip')
        .style('pointer-events', 'none');

      tipSel = tipEnter.merge(tipSel);
      tipSel.each(function () {
        const sel = d3.select(this);
        const triangleSize = chartWidth * 0.04;
        const triangle = d3.symbol().type(d3.symbolTriangle).size(triangleSize);

        sel
          .attr('d', triangle)
          .attr('transform', `translate(${ex},${ey}) rotate(${verticalTransform})`)
          .attr('fill', strokeColor)
          .attr('stroke', strokeColor);
      });
    },
    drawCircle(svg, x, y, radius, fillColor, borderColor, borderSize, className) {
      return svg.append("circle")
        .attr("class", className)
        .attr("cx", x)
        .attr("cy", y)
        .attr("r", radius)
        .style("fill", fillColor)
        .style("stroke", borderColor)
        .style("stroke-width", radius / 5)
        .style("fill-opacity", 0.9)
        .style("opacity", 1);
    },
    writeText(svg, x, y, anchor, fontSize, text, fill = 'white', className = 'none') {
      return svg.append('text')
        .attr('x', x)
        .attr('y', y)
        .attr('text-anchor', anchor)
        .attr('dy', '0.8em')
        .attr('font-weight', 500)
        .attr('class', className)
        .style('font-size', fontSize)
        .style('font-family', this.fontFamily)
        .style('fill', fill)
        .text(text);
    },
    async drawImageBase64 (svg, x, y, width, height, img, idName, rotation = 0) {
      return new Promise((resolve) => {
        if (!this.images[idName]) {
          const baseImage = new Image();
          baseImage.crossOrigin = 'anonymous';
          baseImage.src = img;
          baseImage.onload = async (event) => {
            const dataUrl = await getDataUrl(event.currentTarget);
            this.drawImage(svg, x, y, width, height, dataUrl, idName, rotation);
            resolve(true);
          };
        } else {
          svg.node().appendChild(this.images[idName]);
          resolve(true);
        };
      });
    },
    drawImage(svg, x, y, width, height, img, idName, rotation = 0) {
      const imgNode = svg.append('svg:image');
      const cx = x + width / 2;
      const cy = y + height / 2;
      imgNode
        .attr('x', x)
        .attr('y', y)
        .attr('width', width)
        .attr('height', height)
        .attr('xlink:href', img)
        .attr("transform", rotation ? `rotate(${rotation}, ${cx}, ${cy})` : null);
      this.images[idName] = imgNode.node().cloneNode(true);
    },
    async drawAttackArrow(svg, pitchWidth, pitchHeight) {
      const textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
      const darkCode = this.currentUserFieldColor === 'white' ? '_dark' : '';
      const arrowImg = require(`@/assets/img/attack_direction${darkCode}.png`);
      //Draw arrow first
      const arrowWidth = pitchWidth * 0.12;
      const arrowHeight = arrowWidth * (this.isVertical ? 0.25 : 0.2);
      const arrowX = pitchWidth * (this.isVertical ? -0.025 : 0.04);
      const arrowY = pitchHeight * 0.94;
      const rotation = this.isVertical ? -90 : 0;
      await this.drawImageBase64(svg, arrowX * (this.isVertical ? 0.5 : 1), arrowY * (this.isVertical ? 0.9 : 1), arrowWidth, arrowHeight, arrowImg, 'attackArrow', rotation);

      const textX = this.isVertical ? arrowX + arrowWidth / 2 : arrowX + pitchWidth * 0.09;
      const textY = this.isVertical ? arrowY + arrowHeight + pitchHeight * -0.21 : pitchHeight * 0.945;
      const textFontSize = this.isVertical ? 0.026 * pitchWidth + 'px' : 0.018 * pitchWidth + 'px';
      //Attacking direction text
      const attackDirectionText = this.writeText(svg, textX, textY, this.isVertical ? 'middle' : 'start', textFontSize, this.t('general.attackingDirection'), textColor, 'attackDirectionLabel', 800);
      if (this.isVertical) {
        attackDirectionText.attr('transform', `rotate(-90, ${textX}, ${textY})`);
      };
    },
    async drawLogo(svg, pitchWidth, pitchHeight, margin) {
      const logo = require(`@/assets/img/driblab${this.mediaMode ? "_normal" : ""}_${this.currentUserFieldColor === 'white' ? 'color' : 'white'}.png`);
      const logoWidth = pitchWidth * 0.18;
      const logoHeight = pitchHeight * 0.1;

      const actualSVGWidth = this.isVertical 
        ? (pitchHeight + margin.left + margin.right) 
        : this.SVG_WIDTH;

      const actualSVGHeight = this.isVertical 
        ? (pitchWidth + margin.top + margin.bottom) 
        : (pitchHeight + margin.top + margin.bottom);

      const logoEndX = actualSVGWidth - (actualSVGWidth * 0.09);
      const logoX = logoEndX - logoWidth;
      const logoY = actualSVGHeight * 0.96 - logoHeight / 2;
      await this.drawImageBase64(svg, logoX, logoY, logoWidth, logoHeight, logo, 'logo');
    },
    async printSeasonInfo(svg, pitchWidth, pitchHeight, margin) {
      const textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
      const { game } = this.passes[0];
      const { leagueId, seasonName } = game;
      const crest = 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/leagues/' + leagueId + '.png';
      await this.drawImageBase64(
        svg,
        pitchWidth * 0.07 + margin.left,
        pitchHeight * 0.02 + margin.top,
        pitchWidth * 0.05,
        pitchHeight * 0.05,
        crest,
        'leagueCrest'
      );
      this.writeText(
        svg,
        pitchWidth * 0.12 + margin.left,
        pitchHeight * 0.04 + margin.top,
        'start',
        (0.022 * pitchWidth) + 'px',
        seasonName,
        textColor,
        'SeasonLegendText'
      );
    },
    renderLegend(group, eventLegend = false, passLegend = true, carryLegend = false, ballProgressionLegend = false) {
      const fontColor = this.currentUserFieldColor === 'white' ? "black" : "white";

      const actualSVGWidth = this.isVertical ? (this.PITCH_HEIGHT + this.margin.left + this.margin.right) : this.SVG_WIDTH;
      const actualSVGHeight = this.isVertical ? (this.PITCH_WIDTH + this.margin.top + this.margin.bottom) : (this.PITCH_HEIGHT + this.margin.top + this.margin.bottom);

      const leftX = actualSVGWidth * (this.isVertical ? 0.054 : 0.09);
      const rightX = actualSVGWidth * (this.isVertical ? 0.946 : 0.91);
      const row1Y = actualSVGHeight * (this.isVertical ? 0.026 : 0.05);
      const row2Y = actualSVGHeight * (this.isVertical ? 0.045 : 0.075);

      // Left Column
      const firstText = group.append("text")
        .attr("x", leftX)
        .attr("y", row1Y)
        .attr("text-anchor", "start")
        .style("font-size", actualSVGWidth * (this.isVertical ? 0.024 : 0.018))
        .style("font-weight", 600)
        .style("fill", fontColor)
        .style("font-family", 'NeueMontreal')
        .text(this.legendTitle);

      const firstTextWidth = firstText.node() ? firstText.node().getComputedTextLength() : 0;
      let currentX = leftX + firstTextWidth + 6;

      if (this.legendTeamCrest) {
        group.append("text")
          .attr("x", currentX)
          .attr("y", row1Y)
          .attr("text-anchor", "start")
          .style("font-size", actualSVGWidth * (this.isVertical ? 0.024 : 0.018))
          .style("font-weight", 600)
          .style("fill", fontColor)
          .style("font-family", 'NeueMontreal')
          .text(" · ");
        currentX += 14;

        const crestSize = actualSVGWidth * (this.isVertical ? 0.028 : 0.022);
        group.append("image")
          .attr("xlink:href", this.legendTeamCrest)
          .attr("x", currentX)
          .attr("y", row1Y - (crestSize * 0.75))
          .attr("width", crestSize)
          .attr("height", crestSize
          );
        currentX += crestSize + 8;
      }

      if (this.legendTeamName) {
        group.append("text")
          .attr("x", currentX)
          .attr("y", row1Y)
          .attr("text-anchor", "start")
          .style("font-size", actualSVGWidth * (this.isVertical ? 0.024 : 0.018))
          .style("font-weight", 600)
          .style("fill", fontColor)
          .style("font-family", 'NeueMontreal')
          .text(this.legendTeamName);
      }

      if (this.legendSubtitle) {
        group.append("text")
          .attr("x", leftX)
          .attr("y", row2Y)
          .attr("text-anchor", "start")
          .style("font-size", actualSVGWidth * (this.isVertical ? 0.017 : 0.013))
          .style("fill", fontColor)
          .style("font-family", 'NeueMontreal')
          .style("opacity", 0.85)
          .text(this.legendSubtitle);
      }

      // Right Column
      if (this.legendMetrics) {
        group.append("text")
          .attr("x", rightX)
          .attr("y", row1Y)
          .attr("text-anchor", "end")
          .style("font-size", actualSVGWidth * (this.isVertical ? 0.024 : 0.018))
          .style("font-weight", 600)
          .style("fill", fontColor)
          .style("font-family", this.fontFamily)
          .text(this.legendMetrics.line1);

        group.append("text")
          .attr("x", rightX)
          .attr("y", row2Y)
          .attr("text-anchor", "end")
          .style("font-size", actualSVGWidth * (this.isVertical ? 0.017 : 0.013))
          .style("fill", fontColor)
          .style("font-family", this.fontFamily)
          .style("opacity", 0.85)
          .text(this.legendMetrics.line2);
      }

      const t = (key, params) => {
        try { return this.$t ? this.$t(key, params) : key; } catch (e) { return key; }
      };

      const chartSize = this.PITCH_WIDTH;
      const chartRate = actualSVGWidth / 560;

      let legendCenterX, legendHalfWidth;

      if (this.isVertical) {
        legendCenterX = actualSVGWidth * 0.42; 
        legendHalfWidth = actualSVGWidth * 0.22; 
      } 
      else {
        const arrowRightEdge = 0.08 * actualSVGWidth + 0.3 * actualSVGHeight + 10;
        const logoLeftEdge = 0.76 * actualSVGWidth - 10;
        const availableWidth = logoLeftEdge - arrowRightEdge;
        legendCenterX = Math.min(Math.max(0.56 * actualSVGWidth, arrowRightEdge + availableWidth * 0.5), logoLeftEdge - availableWidth * 0.5);
        legendHalfWidth = availableWidth / 2;
      }

      const textIconGap = 2;

      let x = d3.scaleLinear().domain([20, 92]).range([legendCenterX + legendHalfWidth, legendCenterX - legendHalfWidth]);
      let radiusScalePasses = d3.scaleLinear().domain([0, 1]).range([chartSize * 0.0055, chartSize * 0.024]);
      let radiusPasses = radiusScalePasses(0.02);
      let radiusEvents = radiusScalePasses(0.125);

      let offset = 0;
      let fontSize = 11;

      let baseY = 0.945 * actualSVGHeight; 
      let yOffset = 0.032 * actualSVGHeight;
      let yOffsetMini = 0.004 * actualSVGHeight;

      const textColor = this.currentUserFieldColor === 'white' ? 'black' : "white";
      const fontStyle = {
        family: this.fontFamily,
        size: `${fontSize * chartRate}px`,
        weight: "bold"
      };

      const approxTextWidth = (txt) => txt.length * (fontSize * chartRate * 0.55);

      if (eventLegend) {
        let pxPerUnit = Math.abs(x(1) - x(0));
        let iconGapPx = Math.abs(x(87.5 - textIconGap) - x(89));
        let eventTexts = [t("eventChart.successfulEvent"), t("eventChart.unsuccessfulEvent")];

        let maxEventTextW = Math.max(...eventTexts.map(txt => approxTextWidth(txt)));
        let eventGroupW = (maxEventTextW + iconGapPx) / pxPerUnit + 7;

        this.drawCircleLegend(group, x(89), baseY, radiusEvents, 1, '#28e628', '#28e628');
        this.drawCrossLegend(group, x(89), baseY + yOffset, 45, '#FF0000');

        group.append("text")
          .attr("x", x(87.5 - textIconGap))
          .attr("y", baseY + yOffsetMini)
          .attr("text-anchor", "start")
          .attr("dominant-baseline", "middle")
          .attr("fill", textColor)
          .style("font-family", fontStyle.family)
          .style("font-size", fontStyle.size)
          .style("font-weight", fontStyle.weight)
          .text(eventTexts[0]);

        group.append("text")
          .attr("x", x(87.5 - textIconGap))
          .attr("y", baseY + yOffset + yOffsetMini)
          .attr("text-anchor", "start")
          .attr("dominant-baseline", "middle")
          .attr("fill", textColor)
          .style("font-family", fontStyle.family)
          .style("font-size", fontStyle.size)
          .style("font-weight", fontStyle.weight)
          .text(eventTexts[1]);

        offset = eventGroupW;

        let possessionText = t("eventChart.possessionLost");
        let words = possessionText.split(' ');
        let possLine1 = words[0];
        let possLine2 = words.slice(1).join(' ');
        let possessionTextW = Math.max(approxTextWidth(possLine1), approxTextWidth(possLine2)) / pxPerUnit;
        let possessionIconGap = iconGapPx / pxPerUnit;

        this.drawCircleLegend(group, x(89 - offset), baseY + yOffset * 0.5, radiusEvents, 1, '#FFD600', '#FFD600');

        let possTextX = x(87.5 - offset - textIconGap);
        let lineHeight = yOffset * 0.6;

        group.append("text")
          .attr("x", possTextX)
          .attr("y", baseY + yOffset * 0.5 - lineHeight * 0.5 + yOffsetMini)
          .attr("text-anchor", "start")
          .attr("dominant-baseline", "middle")
          .attr("fill", textColor)
          .style("font-family", fontStyle.family)
          .style("font-size", fontStyle.size)
          .style("font-weight", fontStyle.weight)
          .text(possLine1);

        group.append("text")
          .attr("x", possTextX)
          .attr("y", baseY + yOffset * 0.5 + lineHeight * 0.5 + yOffsetMini)
          .attr("text-anchor", "start")
          .attr("dominant-baseline", "middle")
          .attr("fill", textColor)
          .style("font-family", fontStyle.family)
          .style("font-size", fontStyle.size)
          .style("font-weight", fontStyle.weight)
          .text(possLine2);

        offset += possessionTextW + possessionIconGap + 7;
      }

      if (passLegend) {
        let pxPerUnit = Math.abs(x(1) - x(0));

        this.drawCircleLegend(group, x(89 - offset), baseY, radiusPasses, 0.2, '#28e628', '#28e628');
        this.drawCrossLegend(group, x(89 - offset), baseY + yOffset, 45, '#FF0000');

        this.drawXALineLegend(group, x(88.2 - offset), baseY, x(85.8 - offset), baseY, 2, 1, '#28e628');
        this.drawXALineLegend(group, x(88.8 - offset), baseY + yOffset, x(85.8 - offset), baseY + yOffset, 2, 1, '#FF0000');
        this.drawTriangleLegend(group, x(89 - offset), baseY, x(85.8 - offset), baseY, 35, '#28e628');
        this.drawTriangleLegend(group, x(89 - offset), baseY + yOffset, x(85.8 - offset), baseY + yOffset, 35, '#FF0000');

        group.append("text")
          .attr("x", x(84.5 - offset - textIconGap))
          .attr("y", baseY + yOffsetMini)
          .attr("text-anchor", "start")
          .attr("dominant-baseline", "middle")
          .attr("fill", textColor)
          .style("font-family", fontStyle.family)
          .style("font-size", fontStyle.size)
          .style("font-weight", fontStyle.weight)
          .text(t("eventChart.successfulPass"));

        group.append("text")
          .attr("x", x(84.5 - offset - textIconGap))
          .attr("y", baseY + yOffset + yOffsetMini)
          .attr("text-anchor", "start")
          .attr("dominant-baseline", "middle")
          .attr("fill", textColor)
          .style("font-family", fontStyle.family)
          .style("font-size", fontStyle.size)
          .style("font-weight", fontStyle.weight)
          .text(t("eventChart.unsuccessfulPass"));

        let passTexts = [t("eventChart.successfulPass"), t("eventChart.unsuccessfulPass")];
        let maxPassTextW = Math.max(...passTexts.map(txt => approxTextWidth(txt)));
        let passIconW = Math.abs(x(84.5 - textIconGap) - x(89));
        offset += (maxPassTextW + passIconW) / pxPerUnit + 3;
      }

      if (carryLegend && !ballProgressionLegend) {
        this.drawXALineLegend(group, x(90 - offset), baseY, x(87.8 - offset), baseY, 2, 1, '#b3b7c6');
        this.drawXALineLegend(group, x(90 - offset), baseY + yOffset, x(87.8 - offset), baseY + yOffset, 2, 1, '#b3b7c6');
        this.drawCircleLegend(group, x(87 - offset), baseY, 6, 0.9, '#b3b7c6', '#b3b7c6');
        this.drawCircleLegend(group, x(87 - offset), baseY + yOffset, 6, 0.2, '#b3b7c6', '#b3b7c6');

        let successfulText = t("eventChart.successfulCarry");
        let unsuccessfulText = t("eventChart.unsuccessfulCarry");

        group.append("text")
          .attr("x", x(85.5 - offset - textIconGap))
          .attr("y", baseY + yOffsetMini)
          .attr("text-anchor", "start")
          .attr("dominant-baseline", "middle")
          .attr("fill", textColor)
          .style("font-family", fontStyle.family)
          .style("font-size", fontStyle.size)
          .style("font-weight", fontStyle.weight)
          .text(successfulText);

        group.append("text")
          .attr("x", x(85.5 - offset - textIconGap))
          .attr("y", baseY + yOffset + yOffsetMini)
          .attr("text-anchor", "start")
          .attr("dominant-baseline", "middle")
          .attr("fill", textColor)
          .style("font-family", fontStyle.family)
          .style("font-size", fontStyle.size)
          .style("font-weight", fontStyle.weight)
          .text(unsuccessfulText);
      }
    },
    drawCircleLegend(svg, x, y, radius, opacity, fillColor, borderColor) {
      svg.append("circle")
        .attr("cx", x)
        .attr("cy", y)
        .attr("r", radius)
        .style("fill", fillColor)
        .style("stroke", borderColor)
        .style("stroke-width", 1.5)
        .style("fill-opacity", opacity);
    },
    drawCrossLegend(svg, x, y, angle, color) {
      const cross = d3.symbol().type(d3.symbolCross).size(35);
      svg.append('path')
        .attr('d', cross)
        .attr('transform', `translate(${x},${y}) rotate(${angle})`)
        .attr('fill', color)
        .attr('stroke', color)
        .attr('stroke-width', 0.5);
    },
    drawXALineLegend(svg, x1, y1, x2, y2, strokeWidth, opacity, color) {
      svg.append('line')
        .attr('x1', x1)
        .attr('y1', y1)
        .attr('x2', x2)
        .attr('y2', y2)
        .attr('stroke-width', strokeWidth)
        .attr('stroke', color)
        .style('opacity', opacity);
    },
    drawTriangleLegend(svg, x1, y1, x2, y2, size, color) {
      const triangle = d3.symbol().type(d3.symbolTriangle).size(size);
      const angleRadians = Math.atan2(y2 - y1, x2 - x1);
      const angleDegrees = (angleRadians + (Math.PI / 2)) * (180 / Math.PI);
      svg.append('path')
        .attr('d', triangle)
        .attr('transform', `translate(${x2},${y2}) rotate(${angleDegrees})`)
        .attr('fill', color)
        .attr('stroke', color);
    },
    onResize() {
      const elto = document.getElementById(this.rootElementId);
      if (elto) {
        this.$emit('sizeChange', {
          height: elto.clientHeight,
          width: elto.clientWidth
        });
      }
    }
  },
  destroyed() {
    delete this.observer;
  }
};
</script>